import axios from 'axios';
import Cookies from 'js-cookie';


// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// Create base axios instance with default config (FastApi auth)
const api = axios.create({
  baseURL: process.env.REACT_APP_AUTH_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor for cookie-based auth
api.interceptors.request.use(
  (config) => {
    // Ensure cookies are sent with requests
    config.withCredentials = true;
    // Attach CSRF token for unsafe methods
    const method = (config.method || 'get').toLowerCase();
    const unsafe = ['post', 'put', 'patch', 'delete'];
    if (unsafe.includes(method)) {
      const csrf = Cookies.get('csrf_token');
      if (csrf) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Single-flight refresh
let isRefreshing = false;
let refreshQueue = []; // { resolve, reject, config }

function enqueueRequest(p) {
  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject });
  });
}

function resolveQueue() {
  refreshQueue.forEach(({ resolve }) => resolve());
  refreshQueue = [];
}

function rejectQueue(err) {
  refreshQueue.forEach(({ reject }) => reject(err));
  refreshQueue = [];
}

const AUTH_PATHS = ['/auth/api/token/', '/auth/api/token/refresh/'];


// Response interceptor for handling errors with refresh-retry once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    const status = error.response?.status;

    // If explicitly flagged, bypass refresh logic (e.g., the refresh call itself)
    if (originalRequest.skipAuthRefresh) {
      return Promise.reject(error);
    }


    // Only handle 401s for non-auth endpoints
    const isAuthEndpoint = AUTH_PATHS.some((p) => (originalRequest.url || '').includes(p));
    const onLoginRoute = typeof window !== 'undefined' && /login/i.test(window.location.pathname || '');


    if (status === 401 && !isAuthEndpoint && !onLoginRoute) {

      if (originalRequest._retry) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // If a refresh is already in progress, queue and wait
      if (isRefreshing) {
        await enqueueRequest();
        // After refresh completes, retry original request
        return api(originalRequest);
      }

      // Start refresh
      isRefreshing = true;

      try {
        // Attempt refresh via cookies
        await api.post('/auth/api/token/refresh/', {}, { withCredentials: true, skipAuthRefresh: true });
        // Retry the original request
        isRefreshing = false;
        resolveQueue();
        return api(originalRequest);
      } catch (refreshErr) {
        // Refresh failed: clean up and redirect to login
        isRefreshing = false;
        rejectQueue(refreshErr);
        try { Cookies.remove('user'); } catch (e) { }

        // Optionally: dispatch logout action or emit an event here
        if (!onLoginRoute && typeof window !== 'undefined') {
          window.location.assign('/login');
        }
        return Promise.reject(refreshErr);

      }
    }

    return Promise.reject(error);
  }
);

// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// Auth api like login, logout , isAuthenticated, getUserInfo, getUsers, createUser, updateUser, deleteUser, toggleUserStatus, getAdminStats, changeUserPassword, getUserPermissions, query


const AuthApi = {
  login: async (email, password) => {
    try {

      const response = await api.post('/auth/api/token/', { email, password });

      // Backend now uses HTTP-only cookies, no tokens in response
      const { success, user } = response.data;

      if (success && user) {
        // Get user info (cookies are sent automatically). Do NOT persist role publicly.
        const userResponse = await api.get('/auth/api/user-permissions');

        let FastApiUser = userResponse.data; // { email, role, ... } - role stays server-side usage only

        // **Identify user with Node.js Chat Backend**
        // Construct username for chat backend. Using email if no specific display name.
        // Adjust if your FastApiUser object has a better field for username.
        const chatUsername = FastApiUser.username || FastApiUser.first_name || email.split('@')[0];

        const csrfLogin = Cookies.get('csrf_token');
        const access_token = Cookies.get('access_token');
        const refresh_token = Cookies.get('refresh_token');
        console.log({"access_token": access_token});
        console.log({"refresh_token": refresh_token});
        console.log({"csrfLogin": csrfLogin, "email": email, "chatUsername": chatUsername});
        const identifyResponse = await api.post(
          '/auth/api/chat/users/identify',
          {
            email: FastApiUser.email || email, // Prefer email from FastApiUser, fallback to login email
            username: chatUsername
          },
          {
            withCredentials: true,
            headers: csrfLogin ? { 'X-CSRF-Token': csrfLogin } : {}
          }
        );

        if (!identifyResponse.data.success || !identifyResponse.data.data.user_id) {
          // Clean up FastApi tokens if chat identification fails critically
          // Note: HTTP-only cookies are cleared server-side via logout endpoint
          return { success: false, error: 'Chat user identification failed.' };
        }

        const chatUser = identifyResponse.data.data; // { user_id: "USR_...", email: "...", username: "..." }

        // Store ONLY chat identifiers publicly for client-side access.
        // Do NOT store role or tokens; tokens are HTTP-only cookies.
        const chatOnlyInfo = {
          chatUserId: chatUser.user_id,
          chatUsername: chatUser.username,
          email: FastApiUser.email, // optional, used for display/fallbacks
        };
        Cookies.set('user', JSON.stringify(chatOnlyInfo));

        return { success: true, user: chatOnlyInfo };
      } else {
        // Should not happen if login is successful (2xx) and backend is well-behaved
        return { success: false, error: 'Login failed: Invalid response.' };
      }
    } catch (error) {
      Cookies.remove('user');
      // Note: HTTP-only cookies are cleared server-side
      throw error;
    }
  },

  logout: async () => {
    try {
      // Call backend logout to clear HTTP-only cookies
      await api.post('/auth/api/logout/', {});

      // Clear client-side user info (HTTP-only cookies cleared by server)
      Cookies.remove('user');
      return { success: true };
    } catch (error) {
      // Even if logout API fails, clear local data
      throw error;
    }
  },

  // Logout from all sessions (invalidates refresh tokens server-side)
  logoutAll: async () => {
    try {
      await api.post('/auth/api/logout-all/', {});
      // Clear client-side info (HTTP-only cookies cleared by server)
      Cookies.remove('user');
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  getUserInfo: () => {
    try {
      const userInfoStr = Cookies.get('user');
      if (userInfoStr) {
        const parsedUser = JSON.parse(userInfoStr);
        // Ensure chatUserId exists, return null if critical info is missing
        return parsedUser.chatUserId ? parsedUser : null;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  isAuthenticated: () => {
    const user = AuthApi.getUserInfo(); // Check if user info (with chatUserId) is present
    return !!user && !!user.chatUserId; // Cookies are handled automatically by browser
  },

  // Admin API endpoints for user management
  getUsers: async (params = {}) => {
    try {
      const response = await api.get('/auth/api/admin/users/', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createUser: async (userData) => {
    try {
      const response = await api.post('/auth/api/admin/users/', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/auth/api/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/auth/api/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  toggleUserStatus: async (userId) => {
    try {
      const response = await api.patch(`/auth/api/admin/users/${userId}/toggle-status/`, {});
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAdminStats: async () => {
    try {
      const response = await api.get('/auth/api/admin/stats/');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Change user password (admin only). Pass extra fields (generate_password,
  // must_change_password) through when provided.
  changeUserPassword: async (userId, newPassword, opts = {}) => {
    try {
      const body = { ...opts };
      if (newPassword) body.new_password = newPassword;
      const response = await api.patch(`/auth/api/admin/users/${userId}/change-password/`, body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reset a user's query usage counter to zero (admin only).
  resetUserUsage: async (userId) => {
    try {
      const response = await api.patch(`/auth/api/admin/users/${userId}/reset-usage/`, {});
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Securely fetch user role and permissions (decoded server-side from HTTP-only cookies)
  getUserPermissions: async () => {
    try {
      const response = await api.get('/auth/api/user-permissions');
      return response.data; // { id, email, role, permissions, ... }
    } catch (error) {
      // 401 when not authenticated
      throw error;
    }
  }

};


// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
// ->->->->->->->->->->->->->->->->->->->->->->->->->->->->->->
//qeury api 
const queryApi = async (queryText, customerId = null, autoSend = false) => {
  try {
    const payload = { query: queryText };
    if (customerId) payload.customer_id = customerId;  // conversation context for action verbs
    if (autoSend) payload.auto_send = true;            // UI Auto-send toggle drives the dispatch pipeline
    const response = await api.post('/auth/api/analysis/query/', payload);
    // Return the answer plus the customer this turn resolved to, so the caller
    // can carry conversation focus into the next turn.
    return { answer: response.data.result, customerId: response.data.customer_id || null };
  } catch (error) {
    throw error;
  }
}

// Upload a portfolio file (CSV/XLSX/MD/TXT) for batch prediction + prescription.
// Returns the rendered Markdown answer plus the portfolio metadata the chat
// session records so the loaded portfolio is remembered for the rest of the
// session (e.g. a later "send me the portfolio summary report").
const uploadPortfolioApi = async (file, send = false) => {
  const form = new FormData();
  form.append('file', file);
  form.append('send', send ? 'true' : 'false');
  const response = await api.post('/auth/api/analysis/upload/', form, {
    headers: { 'Content-Type': undefined }, // let axios set the multipart boundary
    withCredentials: true,
  });
  const data = response.data || {};
  return {
    answer: data.result,
    filename: data.filename || file.name,
    customerIds: data.customer_ids_found || [],
    analyzed: data.analyzed || 0,
    totalCustomers: data.total_customers ?? (data.customer_ids_found || []).length,
    totalInvoices: data.total_invoices || 0,
  };
}

// --- Notification center ---
const notificationsApi = {
  list: async (limit = 20) => (await api.get(`/auth/api/notifications/?limit=${limit}`)).data,
  unreadCount: async () => (await api.get('/auth/api/notifications/unread-count/')).data.unread,
  markRead: async (id) => (await api.patch(`/auth/api/notifications/${id}/read/`, {})).data,
  markAllRead: async () => (await api.patch('/auth/api/notifications/read-all/', {})).data,
};

// Clear the server-side session portfolio (called when starting a New Chat) so
// the backend stops injecting the uploaded-portfolio context. Best-effort.
const clearPortfolioApi = async () => {
  try {
    return (await api.post('/auth/api/analysis/portfolio/clear/', {})).data;
  } catch (e) {
    return { success: false };
  }
};

// --- Analytics / Documents / Profile ---
const analyticsApi = async () => (await api.get('/auth/api/analytics/')).data;
const nudgeNotificationApi = async (notificationId) =>
  (await api.post('/auth/api/analytics/nudge/', { notification_id: notificationId })).data;

const documentsApi = async (kind) =>
  (await api.get('/auth/api/documents/' + (kind ? `?kind=${kind}` : ''))).data.documents;

const documentDownloadUrl = (id) =>
  `${api.defaults.baseURL.replace(/\/$/, '')}/auth/api/documents/${id}/download`;

const profileApi = async () => (await api.get('/auth/api/profile/')).data;

// Update own profile (first_name, last_name, time_zone). Returns { success,
// message, profile }. Throws on validation/permission errors (handled by caller).
const updateProfileApi = async (changes) =>
  (await api.patch('/auth/api/profile/', changes)).data;

const changePasswordApi = async (current_password, new_password) =>
  (await api.post('/auth/api/change-password/', { current_password, new_password })).data;

export {
  api, queryApi, uploadPortfolioApi, clearPortfolioApi, AuthApi, notificationsApi,
  analyticsApi, nudgeNotificationApi, documentsApi, documentDownloadUrl, profileApi, updateProfileApi, changePasswordApi,
};
